import { useState } from "react";
import { useFetch } from "hooks/api";
import { Row, Col, Table } from "react-bootstrap";
import moment from "moment";
import Loading from "components/core/blocks/Loading";
import { formatCurrency, formatPercent, formatNumber } from "utils/formatNumber";
import { MarginCRProfilingForm } from "components/custom/programs/templates/pricing/MarginCRProfiling";

const ProgramLog = ({ programId, pricingPlanId, marketPlace, title }) => {
  const [viewableLogId, setViewableLogId] = useState(null);
  const [decisionTableId, setDecisionTableId] = useState(null);

  const { data, isLoading } = useFetch(
    ["program_logs", programId, pricingPlanId],
    "/programslogs/",
    {
      program_id: programId,
      pricing_plan_id: pricingPlanId,
      only_action: true,
    },
    { select: (data) => data?.data?.results ?? [] },
  );

  const LogAction = ({ log }) => {
    const action = log?.meta[2]?.function !== undefined ? log?.meta[2] : log?.meta[1];
    if (!action?.function) {
      return (
        <div>
          <em>We did not perform any actions.</em>
        </div>
      );
    }

    return (
      <div>
        <div>
          <span className="">The action that was performed was:</span>
          <span className="fw-bold ps-2">{action?.function}</span>
        </div>
        <div className="ps-3">
          <span>
            The previous price was:{" "}
            <span className="fw-bold">{formatCurrency(action?.current_price, marketPlace.marketPlace)}</span>
          </span>
        </div>
        <div className="ps-3">
          <span>
            The new price was:{" "}
            <span className="fw-bold">{formatCurrency(action?.next_price, marketPlace.marketPlace)}</span>
          </span>
        </div>
      </div>
    );
  };

  const LogMeta = ({ log }) => {
    const metaValue = log?.meta[2]?.value !== undefined ? log?.meta[2] : log?.meta[1];
    if (!metaValue?.value) {
      return null;
    }
    if (metaValue?.value.length === 0) {
      return null;
    }

    if (metaValue?.value?.length > 0) {
      return (
        <div>
          Dynamic Pricing looked at the {metaValue?.value[0].key} sold from {metaValue?.value[0].from_date} to{" "}
          {metaValue?.value[0].to_date} and these were the daily values:
          <span className="ps-2">{metaValue?.value[0].value.join(", ")}</span>.
        </div>
      );
    }

    return null;
  };

  const LogEntries = ({ log, fullLog }) => {
    const logMatchRegex = /checking \d+?/m;

    const entries = [];
    let foundScope = false;
    let action = fullLog?.meta[2]?.function !== undefined ? fullLog?.meta[2] : fullLog?.meta[1];
    let isHit = false;
    action = action?.function;

    try {
      for (let i = 0; i < log.length; i++) {
        const line = log[i];
        if (line.startsWith("getting scope")) {
          if (!foundScope) {
            const parts = line.split(" ");
            entries.push(
              <>
                <div className="pt-2">{`The last time we performed an action, it was on ${parts[6].replace(",", "")} - ${parts[11]} days ago.`}</div>
                <div className="py-1">The rules on this day were:</div>
              </>,
            );
          }
          foundScope = true;
        } else if (line.startsWith("checking rule Rule")) {
          const f = line.match(/function='([^']+)/)[1];
          const operators = line.match(/operator='([^']+)/g);
          const values = line.match(/values=\[([^\]]+)/g);

          let daysRule = `${f} by ${values[2].replace("values=[", "")}% `;
          daysRule += `if units sold ${operators[0].replace(
            "operator='",
            "",
          )} ${values[0].replace("values=[", "")} days `;
          daysRule += `${operators[2].replace("operator='", "")} `;
          daysRule += `${values[1].replace("values=[", "").split(",")[2].trim()}`;

          isHit = false;
          if (daysRule.includes(action)) {
            isHit = true;
          }
          entries.push(
            <div className={`ps-3`}>
              <span>{daysRule}</span>
            </div>,
          );
        } else if (line.match(logMatchRegex)) {
          const parts = line.split("(");
          const logMatch = parts[0].trim();
          const logParts = logMatch.split(" ");

          if (logParts[2] === ">=") {
            entries.push(
              <div className={`ps-3 pb-3`}>
                Did we sell more than or equal to {logParts[3]} units?
                {isHit ?
                  <span className="fw-bold ps-2" style={{ color: "#007700" }}>
                    Yes we did. We sold {logParts[1]} units.
                  </span>
                : <span className="fw-bold ps-2" style={{ color: "#880000" }}>
                    No we did not.
                  </span>
                }
              </div>,
            );
          } else if (logParts[2] === "<=") {
            entries.push(
              <div className={`ps-3 pb-3`}>
                Did we less more than or equal to {logParts[3]} units?
                {isHit ?
                  <span className="fw-bold ps-2" style={{ color: "#007700" }}>
                    Yes we did. We sold {logParts[1]} units.
                  </span>
                : <span className="fw-bold ps-2" style={{ color: "#880000" }}>
                    No we did not.
                  </span>
                }
              </div>,
            );
          }
        }
      }
    } catch {
      // do nothing
    }
    return entries;
  };

  function parseLogEntries(logEntries) {
    return logEntries.map((entry) => {
      let result = "";

      if (entry.startsWith("checking rule")) {
        const conditionMatches = [
          ...entry.matchAll(/Condition\(operator='(.*?)', values=\['\$\{(.*?)\}', ([0-9.]+)\]/g),
        ];
        const actionMatch = entry.match(/Action\(function='(.*?)', operator='(.*?)', values=\[([0-9.]+)\]/);

        result += "Checking rule with conditions: ";
        result += conditionMatches.map((m) => `${m[2]} ${m[1]} ${m[3]}`).join(", ");

        if (actionMatch) {
          result += ` → Action: ${actionMatch[1]} (${actionMatch[2]} ${actionMatch[3]}%)`;
        } else {
          result += " → No actions";
        }
      } else if (entry.startsWith("getting scope")) {
        const dateMatch = entry.match(/last action (.*?), days since last action ([0-9]+)/);
        if (dateMatch) {
          result += `Scope checked since last action date (${dateMatch[1]}), ${dateMatch[2]} day(s) ago`;
        }
      } else {
        result = entry;
      }

      return result;
    });
  }

  const CRLogDetails = ({ log }) => {
    const formState = {
      defaultValues: log.history,
    };

    return (
      <>
        <div className="pt-3">
          <LogAction log={log} />
        </div>
        <div className="py-1"></div>
        <Row className="py-3">
          <Col xs={2} className="text-center">
            <div
              style={{
                cursor: "pointer",
                border: "1px solid #999",
                backgroundColor: "#ddd",
                borderRadius: "5px",
                padding: "5px",
                width: "150px",
                textAlign: "center",
              }}
              onClick={() => {
                setViewableLogId(viewableLogId === log.id ? null : log.id);
              }}
            >
              View Log Text
            </div>
          </Col>
          <Col xs={2} className="text-center">
            <div
              style={{
                cursor: "pointer",
                border: "1px solid #999",
                backgroundColor: "#ddd",
                borderRadius: "5px",
                padding: "5px",
                width: "150px",
                textAlign: "center",
              }}
              onClick={() => {
                setDecisionTableId(decisionTableId === log.id ? null : log.id);
              }}
            >
              View Decision Table
            </div>
          </Col>
        </Row>

        <div className="py-1">
          {viewableLogId === log.id && (
            <div className="py-3">
              <Table striped bordered hover>
                <tbody>
                  {parseLogEntries(log.meta[0].logs).map((logEntry, i) => (
                    <tr key={i}>
                      <td>{logEntry}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {decisionTableId === log.id && (
            <div className="py-3">
              <MarginCRProfilingForm formState={formState} />
            </div>
          )}
        </div>
      </>
    );
  };

  const Log = ({ log }) => {
    if (log.program.template_type === "MARGIN_PROFILING") {
      return (
        <div className="py-2 my-2">
          <div className="fw-bold p-3" style={{ border: "1px solid #555", backgroundColor: "#ddd" }}>
            {moment(log.ts).format("MMM D, YYYY")}
          </div>
          <div className="py-1">
            <LogEntries log={log?.meta[0]?.logs} fullLog={log} />
          </div>
          <div className="py-1">
            <LogMeta log={log} />
          </div>
          <div className="py-1">
            <LogAction log={log} />
          </div>
        </div>
      );
    } else if (log.program.template_type === "CR_UNITS") {
      if (log.meta.length <= 1 || log.meta[1].value.length === 0) {
        return (
          <div className="py-2 my-2">
            <div className="fw-bold p-3" style={{ border: "1px solid #555", backgroundColor: "#eee" }}>
              {moment(log.ts).format("MMM D, YYYY")}
            </div>
            <CRLogDetails log={log} />
          </div>
        );
      }

      const fromDate = moment(log.meta[1].value[0].from_date);
      const toDate = moment(log.meta[1].value[0].to_date);
      const diffDays = toDate.diff(fromDate, "days");
      const unitList = log.meta[1].value[1].value.slice(-diffDays);
      const unitSum = unitList.reduce((acc, unit) => acc + unit, 0);

      return (
        <div className="py-2 my-2">
          <div className="fw-bold p-3" style={{ border: "1px solid #555", backgroundColor: "#eee" }}>
            {moment(log.ts).format("MMM D, YYYY")}
          </div>
          <div className="py-1">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Long Page Views</th>
                  <th>Long Units</th>
                  <th>Long CR</th>
                  <th>Short Page Views</th>
                  <th>Short Units</th>
                  <th>Short CR</th>
                  <th>Diff</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center">{formatNumber(log.meta[1].value[0].long_page_views)}</td>
                  <td className="text-center">{formatNumber(log.meta[1].value[0].long_orders)}</td>
                  <td className="text-center">{formatPercent(log.meta[1].value[0].long_ocr, 2)}</td>
                  <td className="text-center">{formatNumber(log.meta[1].value[0].short_page_views)}</td>
                  <td className="text-center">{formatNumber(log.meta[1].value[0].short_orders)}</td>
                  <td className="text-center">{formatPercent(log.meta[1].value[0].short_ocr, 2)}</td>
                  <td className="text-center">{formatPercent(log.meta[1].value[0].ocr, 2)}</td>
                </tr>
              </tbody>

              <thead>
                <tr>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th>Unit List</th>
                  <th>Units</th>
                  <th colSpan={3}>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center"> {moment(log.meta[1].value[1].from_date).format("MMM D, YYYY")}</td>
                  <td className="text-center"> {moment(log.meta[1].value[1].to_date).format("MMM D, YYYY")}</td>
                  <td className="text-center">
                    {unitList.map((unit, i) => (
                      <span key={i} className="px-1">
                        {unit}
                      </span>
                    ))}
                  </td>
                  <td className="text-center">{unitSum}</td>
                  <td colSpan={3}>&nbsp;</td>
                </tr>
              </tbody>
            </Table>
          </div>
          <CRLogDetails log={log} />
        </div>
      );
    }

    return "Unknown log type";
  };

  return (
    <>
      <Row className="pt-5">
        <Col>
          <div className="pb-3">
            <span className="fs-4 fw-bold">{title} Logs</span>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          {isLoading && <Loading height={"300px"} fullPage={false} />}
          {!isLoading &&
            data.map((log, i) => (
              <div className="px-3 mb-2" style={{ border: "1px solid #111", borderRadius: "5px" }} key={i}>
                <Log key={i} log={log} />
              </div>
            ))}
        </Col>
      </Row>
    </>
  );
};

export default ProgramLog;
